import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { PRODUCTS } from "@/content/products";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { ExplorerFrame, Swap } from "@/components/site/ExplorerFrame";
import { Container, Eyebrow, Mono, SectionTitle, StatusMarker } from "@/components/site/primitives";
import { ProductFoundation, ProductTop } from "@/components/products/ProductParts";
import { cn } from "@/lib/utils";

const P = PRODUCTS.forma;

const Hero = () => (
  <section data-testid="forma-hero">
    <Container className="grid gap-12 py-14 lg:grid-cols-12 lg:items-center lg:py-20">
      <div className="fade-up lg:col-span-6">
        <Eyebrow index={P.index}>Product · {P.activity}</Eyebrow>
        <h1 data-testid="page-title" className="mt-6 font-display text-2xl font-extrabold uppercase tracking-[0.2em]">Forma</h1>
        <p className="mt-6 font-display text-4xl font-bold leading-[1.02] tracking-[-0.02em] text-o-accent sm:text-5xl lg:text-6xl" data-testid="forma-headline">{P.headline}</p>
        <p className="mt-6 max-w-lg text-o-dim sm:text-lg">{P.tagline}</p>
        <div className="mt-8"><StatusMarker testid="status-forma" /></div>
      </div>
      <Placeholder label={P.placeholder} note="Portrait format" ratio="4 / 5" className="fade-up delay-2 mx-auto w-full max-w-md lg:col-span-5 lg:col-start-8 lg:max-w-none" testid="placeholder-forma-hero" />
    </Container>
  </section>
);

const Mechanism = () => (
  <section className="border-t border-o-line" data-testid="forma-mechanism">
    <Container className="py-16 lg:py-24">
      <Eyebrow index="01">The Formation mechanism</Eyebrow>
      <SectionTitle className="mt-5 max-w-3xl text-3xl sm:text-4xl lg:text-4xl">From general intelligence to an intelligence formed around one person.</SectionTitle>
      <ol className="mt-10 grid gap-3 md:grid-cols-[1fr_auto_1.2fr_auto_1fr] md:items-stretch">
        {P.mechanism.map((m, i) => (
          <li key={m.label} className="contents">
            <div className={cn("flex flex-col justify-between gap-8 p-6", i === 1 ? "bg-o-accent text-o-accent-ink" : "border border-o-line bg-o-surface")}>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] opacity-80">0{i + 1}</span>
              <div>
                <p className="font-display text-2xl font-bold">{m.label}</p>
                <p className={cn("mt-2 text-sm leading-relaxed", i !== 1 && "text-o-dim")}>{m.text}</p>
              </div>
            </div>
            {i < 2 && <ArrowRight aria-hidden className="mx-auto hidden h-5 w-5 self-center text-o-dim md:block" />}
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const Environment = () => {
  const [sel, setSel] = useState(P.environment[0].id);
  const [mode, setMode] = useState("explore");
  const a = P.environment.find((x) => x.id === sel);
  return (
    <ExplorerFrame testid="forma-environment" label="A continuing personal environment" hint="Select an area of Forma's personal environment." mode={mode} onMode={setMode}>
      {mode === "explore" ? (
        <div className="grid md:grid-cols-12">
          <div role="group" aria-label="Areas" className="no-scrollbar flex gap-2 overflow-x-auto border-b border-o-line p-4 md:col-span-4 md:flex-col md:overflow-visible md:border-b-0 md:border-r md:p-5">
            {P.environment.map((x, i) => (
              <button key={x.id} type="button" onClick={() => setSel(x.id)} aria-pressed={sel === x.id} data-testid={`forma-area-${x.id}`}
                className={cn("flex shrink-0 items-baseline gap-3 border px-4 py-3 text-left transition-colors", sel === x.id ? "border-o-accent bg-o-soft" : "border-o-line hover:border-o-strong")}>
                <Mono accent={sel === x.id}>0{i + 1}</Mono>
                <span className="font-display text-base font-bold">{x.label}</span>
              </button>
            ))}
          </div>
          <div className="p-5 sm:p-7 md:col-span-8" aria-live="polite">
            <Swap k={sel}>
              <Placeholder label={a.placeholder} ratio="16 / 10" />
              <h3 className="mt-5 font-display text-2xl font-bold" data-testid="forma-area-title">{a.label}</h3>
              <p className="mt-2 text-o-dim">{a.text}</p>
            </Swap>
          </div>
        </div>
      ) : (
        <dl className="divide-y divide-o-line" data-testid="forma-environment-text">
          {P.environment.map((x) => (
            <div key={x.id} className="grid gap-2 p-5 sm:grid-cols-12 sm:p-7">
              <dt className="font-display text-lg font-bold sm:col-span-4">{x.label}</dt>
              <dd className="text-o-dim sm:col-span-8">{x.text}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="border-t border-o-line px-5 py-3"><span className="draft-tag">Area descriptions are provisional readings of the area names</span></p>
    </ExplorerFrame>
  );
};

export default function FormaPage() {
  return (
    <>
      <ProductTop id="forma" />
      <Hero />
      <Mechanism />
      <section className="border-t border-o-line" data-testid="forma-environment-section">
        <Container className="py-16 lg:py-24">
          <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Eyebrow index="02">Home · Continuity · My World · Thought World · Workstation</Eyebrow>
              <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">An environment that continues with you.</SectionTitle>
            </div>
            <p className="text-o-dim lg:col-span-4 lg:col-start-9">{P.integrationNote}</p>
          </div>
          <Environment />
        </Container>
      </section>
      <ProductFoundation product={P} />
      <EngageBand path="research" from="/products/forma" title="Discuss Forma."
        text="Product collaboration and research inquiries. During Phase 1 this inquiry is an inactive preview." />
    </>
  );
}
